import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileInfoTab } from './ProfileInfoTab';
import { PaymentsTab } from './PaymentsTab';
import { TransactionHistoryTab } from './TransactionHistoryTab';
import { NotificationsTab } from './NotificationsTab';
import { MembershipsTab } from './MembershipsTab';

// The former separate "Funds" and "Transaction history" tabs are consolidated
// into two tabs — "Event History" (entry-centric) and "Financials"
// (money-centric) — so all the registration/payment/financial data lives in
// two predictable places instead of being scattered. Legacy deep-links
// (?tab=funds, ?tab=transactions) resolve onto the new tabs so old bookmarks
// and links keep working.
const VALID_TABS = [
  'profile',
  'memberships',
  'events',
  'financials',
  'notifications',
] as const;
type TabId = (typeof VALID_TABS)[number];

const LEGACY_TAB_ALIASES: Record<string, TabId> = {
  funds: 'events',
  transactions: 'financials',
};

function resolveTab(value: string | null): TabId {
  if ((VALID_TABS as readonly string[]).includes(value ?? '')) {
    return value as TabId;
  }
  if (value && value in LEGACY_TAB_ALIASES) {
    return LEGACY_TAB_ALIASES[value];
  }
  return 'profile';
}

export default function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = resolveTab(searchParams.get('tab'));

  const handleTabChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'profile') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Your account information, registration history, and email preferences.
        </p>
      </div>

      <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3 sm:w-auto sm:inline-flex sm:grid-cols-none">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="memberships">Memberships</TabsTrigger>
          <TabsTrigger value="events">Event History</TabsTrigger>
          <TabsTrigger value="financials">Financials</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-4">
          <ProfileInfoTab />
        </TabsContent>
        <TabsContent value="memberships" className="mt-4">
          <MembershipsTab />
        </TabsContent>
        {/* Event History — every entry the user has made, expandable for
            payment info, status, fee breakdown, and the Stripe/HighCall
            provider badge. Per-row "Pay Now" still lives here. */}
        <TabsContent value="events" className="mt-4">
          <PaymentsTab view="events" />
        </TabsContent>
        {/* Financials — current balance due + pay actions + breakdown
            (PaymentsTab financials slice) followed by the full transaction
            ledger with paid/refunded/owed analytics. One place for the money
            picture. */}
        <TabsContent value="financials" className="mt-4">
          <div className="space-y-6">
            <PaymentsTab view="financials" />
            <TransactionHistoryTab />
          </div>
        </TabsContent>
        <TabsContent value="notifications" className="mt-4">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
